import { FormComponentLoading } from '@/catalog-apps/components/component-library/FormComponent'
import { SingleSelectComboboxField } from '@/components/form-components/SingleSelectCombobox'
import { Button } from '@/components/shadcn/button'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/shadcn/form'
import { Input } from '@/components/shadcn/input'
import { SheetWithFormComponent } from '@/components/ui/SheetWithFormComponent'
import { RestSchemas, useRestClient } from '@/contexts/QueryProvider'
import { zodResolver } from '@hookform/resolvers/zod'
import { useCallback, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { useCurrentApplication } from '../useCurrentApplication'

const appSettingValueOptions = [
  { value: 'KeyVault', label: 'KeyVault' },
  { value: 'string', label: 'string' },
  { value: 'number', label: 'number' },
  { value: 'boolean', label: 'boolean' },
  { value: 'date', label: 'date' },
  { value: 'list', label: 'list' },
] as const

const formSchema = z.object({
  key: z.string().min(1, 'Key is required').max(256, 'Key cannot exceed 256 characters'),
  value: z.enum(['KeyVault', 'string', 'number', 'boolean', 'date', 'list'], {
    message: 'Value is required',
  }),
})

type TApplicationSettingForm = z.infer<typeof formSchema>
type ExistingApplicationSetting = RestSchemas['Messages.Applications.ApplicationSetting']
type NewApplicationSetting = RestSchemas['Messages.Applications.NewApplicationSetting']

type ApplicationSettingFormProps = {
  onSubmit?: () => void
  sheetTrigger?: React.ReactNode
  sheetTriggerTooltip?: string
  sheetTitle?: string
  sheetDescription?: string
  openInSheet?: boolean
  selectedSetting?: ExistingApplicationSetting
}

export const ApplicationSettingForm = (props: ApplicationSettingFormProps) => {
  const [sheetOpen, setSheetOpen] = useState(false)
  const { applicationId } = useCurrentApplication()
  const edit = !!props.selectedSetting
  const { getApplicationsApiGetApplicationSettings, putApplicationsApiCreateApplicationSetting, postApplicationsApiUpdateApplicationSetting } =
    useRestClient()

  const invalidateSettings = useCallback(() => {
    getApplicationsApiGetApplicationSettings.invalidateQueries({
      parameters: {
        query: { applicationId },
      },
    })
  }, [applicationId, getApplicationsApiGetApplicationSettings])

  const { mutate: createSetting, isPending: isCreating } = putApplicationsApiCreateApplicationSetting.useMutation(undefined, {
    onSuccess: () => {
      invalidateSettings()
      if (props.sheetTrigger) setSheetOpen(false)
      toast.success('Application setting created')
      props.onSubmit?.()
    },
    onError: () => {
      toast.error('Failed to create application setting')
    },
  })

  const { mutate: updateSetting, isPending: isUpdating } = postApplicationsApiUpdateApplicationSetting.useMutation(undefined, {
    onSuccess: () => {
      invalidateSettings()
      if (props.sheetTrigger) setSheetOpen(false)
      toast.success('Application setting updated')
      props.onSubmit?.()
    },
    onError: () => {
      toast.error('Failed to update application setting')
    },
  })

  const submitHandler = useCallback(
    (data: TApplicationSettingForm) => {
      const body: NewApplicationSetting = {
        applicationId,
        key: props.selectedSetting?.key ?? data.key,
        type: data.value,
      }

      if (edit) {
        updateSetting({ body })
        return
      }

      createSetting({ body })
    },
    [applicationId, createSetting, edit, props.selectedSetting?.key, updateSetting]
  )

  if (isCreating || isUpdating) return <FormComponentLoading />

  return props.sheetTrigger || props.openInSheet ? (
    <SheetWithFormComponent
      title={props.sheetTitle}
      description={props.sheetDescription}
      trigger={props.sheetTrigger}
      triggerTooltip={props.sheetTriggerTooltip}
      wide
      isOpen={sheetOpen}
      onOpenChange={setSheetOpen}
    >
      <FormComponent {...props} edit={edit} submitHandler={submitHandler} />
    </SheetWithFormComponent>
  ) : (
    <FormComponent {...props} edit={edit} submitHandler={submitHandler} />
  )
}

function FormComponent({
  selectedSetting,
  submitHandler,
  edit,
}: ApplicationSettingFormProps & {
  edit: boolean
  submitHandler: (data: TApplicationSettingForm) => void
}) {
  const defaultValues = useMemo(
    () => ({
      key: selectedSetting?.key ?? '',
      value: (selectedSetting?.type as TApplicationSettingForm['value']) ?? 'string',
    }),
    [selectedSetting?.key, selectedSetting?.type]
  )

  const form = useForm<TApplicationSettingForm>({
    resolver: zodResolver(formSchema),
    defaultValues,
  })

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(submitHandler)}>
        <div className="flex h-full flex-col">
          <div className="space-y-6">
            <FormField
              control={form.control}
              name="key"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Setting key</FormLabel>
                  <FormControl>
                    <Input {...field} disabled={edit} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <SingleSelectComboboxField
              control={form.control}
              name="value"
              label="Value"
              options={appSettingValueOptions}
              placeholder="Select value type..."
              searchPlaceholder="Search value types..."
              emptyMessage="No value types found."
              allowClear={false}
            />
          </div>
          <div className="form-action-container">
            <Button type="submit">{edit ? 'Update' : 'Create'}</Button>
          </div>
        </div>
      </form>
    </Form>
  )
}
